import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { SecurityCard } from '../ui/SecurityCard'
import { Button } from '../ui/Button'
import {
  fetchExecutiveOverview,
  recomputeExecutiveGovernance,
  executiveGovernanceStandardPath,
  type ExecutivePosture,
} from '../../services/executiveGovernanceApi'
import styles from '../../styles/pages/home/Home.module.scss'

function levelColor(level?: string): string {
  switch (level) {
    case 'CRITICAL':
    case 'AT_RISK':
      return '#ef4444'
    case 'DEGRADED':
    case 'DECLINING':
      return '#f59e0b'
    case 'WATCH':
    case 'STABLE':
    case 'IMPROVING':
      return '#10b981'
    default:
      return '#56a3d9'
  }
}

function trajectoryPercent(traj?: string): number {
  switch (traj) {
    case 'IMPROVING':
      return 78
    case 'STABLE':
      return 55
    case 'DECLINING':
      return 35
    case 'CRITICAL':
      return 15
    default:
      return 50
  }
}

export default function HomeExecutiveGovernanceSection() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [loading, setLoading] = React.useState(true)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [posture, setPosture] = React.useState<ExecutivePosture | null>(null)

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchExecutiveOverview()
      setPosture(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load executive governance')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void load()
  }, [load])

  const handleRecompute = async () => {
    setBusy(true)
    try {
      await recomputeExecutiveGovernance()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Recompute failed')
    } finally {
      setBusy(false)
    }
  }

  if (loading && !posture) {
    return (
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          {t('executiveGovernance.executiveOversight', 'Executive Oversight')}
        </h2>
        <p className="text-sm text-gray-500">{t('common.loading', 'Loading…')}</p>
      </section>
    )
  }

  const stability = posture?.certificationRiskLevel || 'STABLE'
  const trajectory = posture?.readinessTrajectory || 'STABLE'
  const instabilityBand = posture?.instability?.band || 'LOW'
  const topBlast = posture?.blastRadius?.impacts?.[0]
  const mostDegraded = (
    posture?.pressure as { mostDegradedStandards?: Array<{ standardCode: string; totalPressure: number }> }
  )?.mostDegradedStandards

  return (
    <section className={styles.section} data-testid="home-executive-governance">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className={styles.sectionTitle}>
          {t('executiveGovernance.executiveOversight', 'Executive Oversight')}
        </h2>
        <div className="flex gap-2">
          <Button type="button" color="outline" size="small" disabled={busy} onClick={() => void handleRecompute()}>
            {t('executiveGovernance.recompute', 'Recompute')}
          </Button>
          <Button
            type="button"
            color="brand"
            size="small"
            onClick={() => navigate('/compliance-center/executive-governance')}
          >
            {t('executiveGovernance.executiveGovernance', 'Executive Governance')}
          </Button>
        </div>
      </div>

      {error ? <div className={styles.errorBanner}>{error}</div> : null}

      <div className="dashboard-row">
        <SecurityCard
          size="large"
          title={t('executiveGovernance.executiveOperationalPosture', 'Executive Operational Posture')}
          percent={Math.round(posture?.governanceStability ?? 50)}
          color={levelColor(stability)}
          statusLabel={t('executiveGovernance.certificationStability', 'Certification Stability')}
          statusText={stability}
          statusColor={levelColor(stability)}
          hoverDetail={posture?.explanations?.summary}
          className={styles.securityCardLarge}
        />
        <SecurityCard
          size="small"
          title={t('executiveGovernance.readinessTrajectory', 'Readiness Trajectory')}
          percent={trajectoryPercent(trajectory)}
          color={levelColor(trajectory)}
          statusText={trajectory}
          hoverDetail={posture?.trajectory?.deltaScore != null ? `Δ ${posture.trajectory.deltaScore}` : undefined}
          className={styles.securityCardSmall}
        />
        <SecurityCard
          size="small"
          title={t('executiveGovernance.governanceInstability', 'Governance Instability')}
          percent={posture?.instability?.instabilityIndex ?? 0}
          color={levelColor(
            instabilityBand === 'HIGH' ? 'CRITICAL' : instabilityBand === 'ELEVATED' ? 'DEGRADED' : 'STABLE',
          )}
          statusText={instabilityBand}
          className={styles.securityCardSmall}
        />
        <SecurityCard
          size="small"
          title={t('executiveGovernance.operationalPressure', 'Operational Pressure')}
          percent={Math.min(100, Math.round((posture?.overallOperationalPressure ?? 0) * 100))}
          color="#3b82f6"
          statusText={`${posture?.degradedControlCount ?? 0} ${t('executiveGovernance.degradedControls', 'degraded')}`}
          className={styles.securityCardSmall}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <h3 className="font-medium text-textMain m-0 mb-2">
            {t('executiveGovernance.blastRadius', 'Blast Radius')}
          </h3>
          {topBlast ? (
            <p className="text-xs text-gray-600 m-0">{topBlast.summary}</p>
          ) : (
            <p className="text-xs text-gray-500 m-0 italic">
              {t('executiveGovernance.noBlastRadius', 'No shared degraded controls in scope.')}
            </p>
          )}
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <h3 className="font-medium text-textMain m-0 mb-2">
            {t('executiveGovernance.pressureConcentration', 'Pressure Concentration')}
          </h3>
          <ul className="text-xs text-gray-600 m-0 p-0 list-none space-y-1">
            {mostDegraded?.slice(0, 4).map((s) => (
              <li key={s.standardCode}>
                <button
                  type="button"
                  className="text-left text-blue-700 hover:underline bg-transparent border-0 p-0 cursor-pointer text-xs"
                  onClick={() => navigate(executiveGovernanceStandardPath(s.standardCode))}
                >
                  {s.standardCode}: {Math.round(s.totalPressure * 100) / 100}
                </button>
              </li>
            )) ?? <li className="italic">{t('common.none', 'None')}</li>}
          </ul>
        </div>
      </div>

      {posture?.explanations?.lines?.length ? (
        <ul className="text-xs text-slate-600 list-disc pl-5 m-0 space-y-1">
          {posture.explanations.lines.slice(0, 4).map((line) => (
            <li key={line.code}>{line.message}</li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
